import React, { useState, useEffect } from 'react';
import { getTeamGear, equipGearToCharacterSheet, unequipGear, getTeamData, getWeekStatus } from '../DatabaseAPI';
import { getSeasonRoster, getSeasonDashboard } from '../api/seasonApi';
import PlayerAccordionTab from '../components/gear/PlayerAccordionTab';
import UnheldAccordionTab from '../components/gear/UnheldAccordionTab';
import { canPlayerAcceptGear as sharedCanPlayerAcceptGear } from '../utils/gearRules';
import TutorialPopup from '../components/tutorial/TutorialPopup';
import { Button } from '../components/ui';
import './GearPage.css';

/**
 * THE SEASON BRANCH BELOW IS LIVE (2026-08-28).
 *
 * Season teams were previously gated out of gear in three places
 * (`SEASON_UNSUPPORTED_PAGES` in the MythicFootball shell, the SeasonTeamPage
 * GEAR tab, and the `gearPage` route in AppRouter) because the gear UI is keyed
 * on `CharacterSheet` end to end and `serialize_roster()` omitted that id: every
 * season player bucketed under one `undefined` key and EQUIPPED gear matched no
 * bucket at all, disappearing from the page.
 *
 * The season roster now returns `character_sheet_id`, so all three gates were
 * removed and this branch renders for real.
 *
 * GEAR IS SHARED TEAM INVENTORY IN BOTH MODES — there is no season gear model,
 * inventory or API, and there must not be one. `Gear` hangs off `Team` and is
 * equipped to a `CharacterSheet`, neither of which is weekly-specific.
 *
 * The ONLY thing that differs by mode is HOW THE PLAYER LIST IS LOADED:
 *
 *   WEEKLY → getTeamData(teamId)      (TeamStatsView; runs assert_weekly_league)
 *   SEASON → getSeasonRoster(teamId)  (the season roster endpoint)
 *
 * That split exists because `TeamStatsView.get()` calls `assert_weekly_league()`
 * and REJECTS a season league outright — it is not that season needs different
 * gear, it is that the weekly player-loading endpoint refuses to answer for a
 * season team. Everything downstream of the adapter — getTeamGear, equip,
 * unequip, the accordions, transfer mode — is mode-agnostic and untouched.
 */
const isSeasonMode = () => sessionStorage.getItem('selectedLeagueMode') === 'SEASON';

/**
 * Season week statuses during which the backend REFUSES roster/gear mutations.
 *
 * Mirrors `FROZEN_STATUSES` in backend/football/season/mutation_guard.py
 * verbatim. `SeasonMutationGuard.assert_gear_mutable()` resolves the governing
 * SeasonWeek and raises `SeasonMutationLocked` when its status is one of these.
 *
 * ⚠️ THIS LIST IS A MIRROR, NOT A SOURCE OF TRUTH. It exists only so the UI can
 * disable controls and say WHY before the user clicks. The server remains the
 * authority: every equip/unequip still goes to the backend and its 409 envelope
 * is surfaced verbatim. If this list ever drifts from the backend's, the
 * backend wins and the user sees its error — the failure mode is a pointless
 * round-trip, never an unauthorised write.
 */
const SEASON_FROZEN_WEEK_STATUSES = ['LOCKED', 'SCORING', 'FINALIZED'];

/**
 * Adapt one season roster row into the player shape the gear accordions need.
 *
 * ⚠️ NEVER ADD A `|| row.league_player_id` FALLBACK TO `character_sheet_id`.
 * The weekly adapter's `p.character_sheet_id || p.id` fallback is safe there
 * because both ids describe the same weekly player payload. Here they are two
 * DIFFERENT TABLES: `/v2/gear/<id>/equip/` expects a **CharacterSheet** PK, and
 * substituting a **LeaguePlayer** id would either 404 or — far worse — equip
 * gear onto an unrelated player's sheet whose id happens to collide.
 *
 * `serialize_roster()` supplies the real id (`CharacterSheet` is a OneToOne on
 * `LeaguePlayer`, so exactly one exists per roster row). If it is ever absent
 * the row must render read-only rather than mis-target a write, which is what
 * the explicit guard in `handleEquipGear()` enforces.
 */
function seasonRosterRowToGearPlayer(row) {
    return {
        id: row.league_player_id,
        name: row.nfl_player_name,
        // NOT `|| row.league_player_id` — see the warning above.
        character_sheet_id: row.character_sheet_id,
        position: row.position || 'UNKNOWN',
        gsis_id: row.gsis_id,
        source_player_id: row.source_player_id,
        presentation: row.presentation,
    };
}

function GearPage({ user, setPage, weekStatus: propWeekStatus }) {
  const [teamGear, setTeamGear] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [weekStatus, setWeekStatus] = useState(propWeekStatus || null);
  // Season lifecycle equivalent of `weekStatus`, from the season dashboard.
  // { week, status } for the active SeasonWeek, or null when the league has no
  // scheduled week yet (FORMING/DRAFTING) or the read failed.
  const [seasonWeek, setSeasonWeek] = useState(null);
  const [teamPlayers, setTeamPlayers] = useState([]);
  const [expandedTabs, setExpandedTabs] = useState(new Set([0])); // First player expanded by default
  const [transferMode, setTransferMode] = useState({
    active: false,
    gearId: null,
    sourceCharacterSheetId: null,
    gearSlot: null,
    swappableGearIds: []
  });

  // Tutorial State
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialData] = useState({
    page_identifier: 'gear_page',
    title: 'Managing Your Gear',
    content_html: `
      <p>Go to the <strong>"Unheld"</strong> section to see your available gear.</p>
      <p>You can click on the <strong>rectangle icon</strong> <svg width="30" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display: inline-block; vertical-align: middle; margin: 0 4px;"><rect x="3" y="3" width="18" height="18" rx="2"/></svg> to assign gear directly or the <strong>circle icon</strong> <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display: inline-block; vertical-align: middle; margin: 0 4px;"><circle cx="12" cy="12" r="9"/></svg> to swap gear for gear.</p>
    `,
    image_url: null
  });

  // Check if gear modifications are allowed based on league state.
  //
  // TWO LIFECYCLES, ONE QUESTION. Weekly asks `stats_active` on the League;
  // season asks the active SeasonWeek's status. They are different fields on
  // different models and must not be substituted for one another — that is why
  // getWeekStatus() is never called for a season team.
  //
  // Both are a UX pre-check ONLY. The server is the authority in both modes:
  // GearEquipService runs SeasonMutationGuard and returns a 409 envelope that
  // the handlers below surface verbatim. Disabling controls here just avoids
  // inviting a click that is already known to fail.
  //
  // Unknown → allowed, deliberately. If a status could not be read we do NOT
  // pre-block the user; we let them try and let the backend answer. Blocking on
  // absent data would strand a user whose read merely failed.
  const seasonWeekIsFrozen = Boolean(
    seasonWeek && SEASON_FROZEN_WEEK_STATUSES.includes(seasonWeek.status)
  );
  const canModifyGear = isSeasonMode()
    ? !seasonWeekIsFrozen
    : (weekStatus ? !weekStatus.stats_active : true);

  // Equipment limits and the eligibility rule now live in `utils/gearRules`.
  // The comment that used to sit here ("same as GearItemCard") was the warning
  // sign: the two copies had in fact drifted — GearItemCard's check omitted the
  // 3-item roster cap entirely, so its modal offered equips this page would
  // have refused. One definition now serves every surface.

  // Helper function to sort players by position (QB, RB, WR)
  const sortPlayersByPosition = (players) => {
    const positionOrder = { 'QB': 1, 'RB': 2, 'WR': 3 };
    
    return [...players].sort((a, b) => {
      const orderA = positionOrder[a.position] || 999;
      const orderB = positionOrder[b.position] || 999;
      return orderA - orderB;
    });
  };

  useEffect(() => {
    let teamId = user?.id;
    
    // If no teamId from user prop, try sessionStorage as fallback
    if (!teamId) {
      const storedTeamId = sessionStorage.getItem('selectedTeamId');
      if (storedTeamId) {
        console.log('GearPage: Recovered teamId from sessionStorage:', storedTeamId);
        teamId = storedTeamId;
      }
    }
    
    if (!teamId) {
      setError('No team ID provided');
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);

        // MODE-AWARE PLAYER LOAD. Only the player list differs by mode; the
        // gear reads/writes below are shared and identical for both.
        const seasonMode = isSeasonMode();
        let players = null;

        if (seasonMode) {
          // getTeamData() would 4xx here — TeamStatsView runs
          // assert_weekly_league() and rejects season leagues.
          //
          // getSeasonRoster() resolves to the ROSTER ARRAY ITSELF, not to the
          // `{ roster: [...] }` envelope — the wrapper already unwrapped it via
          // requireArray(payload, 'roster', …). Do NOT reach for `.roster` here;
          // it is undefined and `.map()` throws, which surfaced as "Failed to
          // load data" on every season visit to GEAR.
          const seasonRoster = await getSeasonRoster(teamId);
          players = seasonRoster.map(seasonRosterRowToGearPlayer);
          console.log(`🏆 GearPage: loaded ${players.length} SEASON roster player(s) for team ${teamId}`);
        } else {
          const teamData = await getTeamData(teamId);
          players = teamData && teamData.players
            ? teamData.players.map(p => ({
                id: p.id,
                name: p.name,
                character_sheet_id: p.character_sheet_id || p.id,
                position: p.position || 'UNKNOWN',
                gsis_id: p.gsis_id,
                source_player_id: p.source_player_id,
                presentation: p.presentation,
              }))
            : null;
        }

        if (players) {
          // Sort players by position (QB, RB, WR)
          const sortedPlayers = sortPlayersByPosition(players);
          setTeamPlayers(sortedPlayers);
          
          // Check if we should expand a specific player (from PlayerPage navigation)
          const expandPlayerId = sessionStorage.getItem('expandPlayerOnGearPage');
          if (expandPlayerId) {
            // Find the index of the player to expand
            const playerIndex = sortedPlayers.findIndex(p =>
              p.character_sheet_id === parseInt(expandPlayerId) ||
              p.id === parseInt(expandPlayerId)
            );
            
            if (playerIndex !== -1) {
              // Expand this player's accordion
              setExpandedTabs(new Set([playerIndex]));
            }
            
            // Clear the stored value so it doesn't persist on next visit
            sessionStorage.removeItem('expandPlayerOnGearPage');
          }
        }

        // Load team gear
        await loadTeamGear(teamId);

        // Load the lifecycle state that governs whether gear may be modified.
        // EACH MODE READS ITS OWN — never the other's.
        const leagueId = sessionStorage.getItem('selectedLeagueId');

        if (seasonMode) {
          // The weekly getWeekStatus() is NEVER called for a season team: it
          // returns `League.current_week`/`stats_active`, which do not govern a
          // season league at all. The season equivalent is the active
          // SeasonWeek's status from the dashboard.
          if (leagueId) {
            try {
              const dashboard = await getSeasonDashboard(leagueId);
              // `current_week` is null while the league is FORMING/DRAFTING —
              // genuinely no week, not a failure.
              setSeasonWeek(dashboard.current_week || null);
              console.log(
                '🏆 GearPage: season week for gear permissions:',
                dashboard.current_week || '(no scheduled week yet)'
              );
            } catch (dashboardError) {
              // A failed lifecycle read must not block the page or silently
              // pretend the week is open — it leaves the status UNKNOWN, the
              // controls stay enabled, and the server arbitrates on write.
              console.error(
                '⚠️ GearPage: could not read season week status; leaving gear controls enabled ' +
                  'and deferring to the server mutation guard:',
                dashboardError
              );
              setSeasonWeek(null);
            }
          }
        } else if (!propWeekStatus) {
          if (leagueId) {
            const status = await getWeekStatus(leagueId);
            setWeekStatus(status);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load data');
        setLoading(false);
      }
    };

    loadData();
    
    // Check if we should show the tutorial (e.g., after draft completion)
    const showGearTutorial = sessionStorage.getItem('showGearTutorial');
    if (showGearTutorial === 'true') {
      setShowTutorial(true);
      sessionStorage.removeItem('showGearTutorial'); // Clear flag after showing
    }
  }, [user?.id, propWeekStatus]);

  const loadTeamGear = async (teamId) => {
    try {
      const gear = await getTeamGear(teamId);
      setTeamGear(gear || []);
      return gear;
    } catch (error) {
      console.error('Error loading team gear:', error);
      throw error;
    }
  };

  // Organize gear by player
  const organizeGearByPlayer = () => {
    const organized = {};
    
    // Initialize each player with empty array
    teamPlayers.forEach(player => {
      organized[player.character_sheet_id] = [];
    });
    
    // Add "UNHELD" category
    organized['UNHELD'] = [];
    
    // Sort gear into categories
    teamGear.forEach(item => {
      if (item.character_sheet) {
        // Gear is assigned to a player
        if (organized[item.character_sheet]) {
          organized[item.character_sheet].push(item);
        }
      } else {
        // Gear is unassigned
        organized['UNHELD'].push(item);
      }
    });
    
    return organized;
  };

  const handleEquipGear = async (gearId, characterSheetId) => {
    if (!canModifyGear) {
      return;
    }

    // NEVER POST an undefined target. `/v2/gear/<id>/equip/` requires a real
    // CharacterSheet PK: sending undefined would 400 with an opaque serializer
    // error, and sending a substituted LeaguePlayer id could equip onto the
    // WRONG player's sheet. Both roster contracts supply the id, so reaching
    // here means the payload is malformed — fail loudly rather than guessing.
    if (characterSheetId === undefined || characterSheetId === null) {
      const message =
        'Cannot equip: this roster row has no character sheet id. Expected the ' +
        'roster payload to carry character_sheet_id for every player.';
      console.error(`❌ GearPage: ${message}`, { gearId, seasonMode: isSeasonMode() });
      alert(message);
      return;
    }

    try {
      await equipGearToCharacterSheet(gearId, characterSheetId);

      // Refresh data from server
      const teamId = user?.id || sessionStorage.getItem('selectedTeamId');
      if (teamId) {
        await loadTeamGear(teamId);
      }
    } catch (error) {
      console.error('Error equipping gear:', error);
      alert('Failed to equip gear: ' + (error.message || error));
    }
  };

  const handleUnequipGear = async (gearId) => {
    if (!canModifyGear) {
      return;
    }

    try {
      await unequipGear(gearId);

      // Refresh data from server
      const teamId = user?.id || sessionStorage.getItem('selectedTeamId');
      if (teamId) {
        await loadTeamGear(teamId);
      }
    } catch (error) {
      console.error('Error unequipping gear:', error);
      alert('Failed to unequip gear: ' + (error.message || error));
    }
  };

  // Thin adapter over the shared rule, preserving this page's existing
  // (characterSheetId, slot, teamGear) argument order for its call sites.
  const canPlayerAcceptGear = (characterSheetId, slot, currentTeamGear) =>
    sharedCanPlayerAcceptGear(currentTeamGear, characterSheetId, slot);

  // Calculate tab states for transfer mode
  const calculateTabStates = () => {
    if (!transferMode.active) return {};
    
    const states = {};
    const sourceId = transferMode.sourceCharacterSheetId;
    
    // Check each player
    teamPlayers.forEach(player => {
      const characterSheetId = player.character_sheet_id;
      
      // Source player = RED (cancel action)
      if (characterSheetId === sourceId) {
        states[characterSheetId] = 'source';
        return;
      }
      
      // Check if player can accept this gear
      const canAccept = canPlayerAcceptGear(
        characterSheetId,
        transferMode.gearSlot,
        teamGear
      );
      
      states[characterSheetId] = canAccept ? 'valid' : 'invalid';
    });
    
    // UNHELD tab
    if (sourceId === null) {
      states['UNHELD'] = 'source'; // Already in UNHELD
    } else {
      states['UNHELD'] = 'valid'; // Can always unequip
    }
    
    return states;
  };

  // Calculate which gear items can be swapped with the selected gear
  const calculateSwappableGear = (selectedGearId, selectedSlot) => {
    if (!selectedGearId || !selectedSlot) return [];
    
    // Find all gear items in the same slot (excluding the selected one)
    return teamGear
      .filter(g => g.slot === selectedSlot && g.id !== selectedGearId)
      .map(g => g.id);
  };

  // Handle transfer button click
  const handleTransferClick = (gearId, sourceCharacterSheetId, gearSlot) => {
    // If called with null values, cancel transfer mode
    if (gearId === null) {
      setTransferMode({
        active: false,
        gearId: null,
        sourceCharacterSheetId: null,
        gearSlot: null,
        swappableGearIds: []
      });
      return;
    }
    
    // Calculate swappable gear IDs
    const swappableIds = calculateSwappableGear(gearId, gearSlot);
    
    setTransferMode({
      active: true,
      gearId,
      sourceCharacterSheetId,
      gearSlot,
      swappableGearIds: swappableIds
    });
  };

  // Handle tab click (modified to support transfer mode and multiple open tabs)
  const handleTabClick = (tabIndex, targetCharacterSheetId) => {
    // If in transfer mode, handle transfer logic
    if (transferMode.active) {
      const tabStates = calculateTabStates();
      const tabState = tabStates[targetCharacterSheetId];
      
      if (tabState === 'source' || tabState === 'invalid') {
        // Cancel transfer mode
        setTransferMode({
          active: false,
          gearId: null,
          sourceCharacterSheetId: null,
          gearSlot: null,
          swappableGearIds: []
        });
        return;
      }
      
      if (tabState === 'valid') {
        // Execute transfer
        handleTransferExecution(targetCharacterSheetId);
        return;
      }
    }
    
    // Normal tab click behavior (toggle tab in/out of expanded set)
    setExpandedTabs(prevExpanded => {
      const newExpanded = new Set(prevExpanded);
      if (newExpanded.has(tabIndex)) {
        newExpanded.delete(tabIndex);
      } else {
        newExpanded.add(tabIndex);
      }
      return newExpanded;
    });
  };

  // Execute gear transfer
  const handleTransferExecution = async (targetCharacterSheetId) => {
    try {
      if (targetCharacterSheetId === 'UNHELD') {
        // Unequip gear
        await unequipGear(transferMode.gearId);
      } else {
        // Equip to target player
        await equipGearToCharacterSheet(
          transferMode.gearId,
          targetCharacterSheetId
        );
      }
      
      // Refresh data
      const teamId = user?.id || sessionStorage.getItem('selectedTeamId');
      if (teamId) {
        await loadTeamGear(teamId);
      }
      
      // Exit transfer mode
      setTransferMode({
        active: false,
        gearId: null,
        sourceCharacterSheetId: null,
        gearSlot: null,
        swappableGearIds: []
      });
    } catch (error) {
      console.error('Transfer failed:', error);
      alert('Failed to transfer gear: ' + (error.message || error));
      
      // Exit transfer mode on error
      setTransferMode({
        active: false,
        gearId: null,
        sourceCharacterSheetId: null,
        gearSlot: null,
        swappableGearIds: []
      });
    }
  };

  // Handle gear swap between two players
  const handleGearSwap = async (targetGearId) => {
    try {
      const sourceGear = teamGear.find(g => g.id === transferMode.gearId);
      const targetGear = teamGear.find(g => g.id === targetGearId);
      
      if (!sourceGear || !targetGear) {
        throw new Error('Gear items not found');
      }
      
      // Store the character sheet IDs before unequipping
      const sourceCharacterSheet = sourceGear.character_sheet;
      const targetCharacterSheet = targetGear.character_sheet;
      
      // Step 1: Unequip both items first to avoid slot limit issues
      await unequipGear(sourceGear.id);
      await unequipGear(targetGear.id);
      
      // Step 2: Equip each item to its new player
      await equipGearToCharacterSheet(
        sourceGear.id,
        targetCharacterSheet
      );
      
      await equipGearToCharacterSheet(
        targetGear.id,
        sourceCharacterSheet
      );
      
      // Refresh data
      const teamId = user?.id || sessionStorage.getItem('selectedTeamId');
      if (teamId) {
        await loadTeamGear(teamId);
      }
      
      // Exit transfer mode
      setTransferMode({
        active: false,
        gearId: null,
        sourceCharacterSheetId: null,
        gearSlot: null,
        swappableGearIds: []
      });
    } catch (error) {
      console.error('Swap failed:', error);
      alert('Failed to swap gear: ' + (error.message || error));
      
      // Exit transfer mode on error
      setTransferMode({
        active: false,
        gearId: null,
        sourceCharacterSheetId: null,
        gearSlot: null,
        swappableGearIds: []
      });
    }
  };

  const handleCloseTutorial = () => {
    setShowTutorial(false);
  };

  const handleMarkTutorialViewed = async (pageIdentifier, neverShowAgain) => {
    console.log('Tutorial marked as viewed:', pageIdentifier, 'Never show again:', neverShowAgain);
    // For now, just log - backend integration will come later
    if (neverShowAgain) {
      localStorage.setItem('tutorials_disabled', 'true');
    }
    localStorage.setItem(`tutorial_viewed_${pageIdentifier}`, 'true');
  };

  if (loading) {
    return (
      <div className="gear-page-loading">
        <h2>Loading Gear...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gear-page-error">
        <h2>Error: {error}</h2>
        <Button
          onClick={() => setPage('TEAM')}
          className="error-back-button"
          variant="secondary"
        >
          Back to Team
        </Button>
      </div>
    );
  }

  const organizedGear = organizeGearByPlayer();
  const tabStates = calculateTabStates();

  return (
    <div className="gear-page">
      {/* Navigation Header */}
      <div className="gear-nav-header">
        <Button
          className="nav-button"
          onClick={() => setPage('HOME')}
          variant="secondary"
        >
          LEAGUES
        </Button>
        <Button
          className="nav-button"
          onClick={() => setPage('TEAM')}
          variant="secondary"
        >
          TEAM
        </Button>
        {/* STANDINGS is mode-specific. 'LEAGUE' resolves to the WEEKLY
            standingsPage, which ranks Team.wins/losses and the current week's
            WeeklyTeamStats — nothing on that endpoint rejects a season league,
            so it renders a plausible but silently WRONG table. Season standings
            come from SEALED SeasonTeamWeekResult rows via SeasonStandingsPage. */}
        <Button
          className="nav-button"
          onClick={() => setPage(isSeasonMode() ? 'SEASON_STANDINGS' : 'LEAGUE')}
          variant="secondary"
        >
          STANDINGS
        </Button>
        <Button
          className="nav-button active"
          onClick={() => setPage('GEAR')}
          variant="secondary"
        >
          GEAR
        </Button>
      </div>

      {/* Main Content */}
      <div className="gear-page-content">
        {/* Gear modification lock warning.
            The REASON is mode-specific — the old copy hardcoded the weekly one
            ("League stats are active"), which would be simply untrue on a
            season team whose week is LOCKED/SCORING/FINALIZED. Naming the real
            week and status also tells the user WHEN it will reopen. */}
        {!canModifyGear && (
          <div className="gear-locked-warning">
            <div className="gear-locked-icon">🔒</div>
            <div className="gear-locked-text">
              GEAR MODIFICATIONS LOCKED
              <div className="gear-locked-subtext">
                {seasonWeekIsFrozen
                  ? `Week ${seasonWeek.week} is ${seasonWeek.status.replace('_', ' ').toLowerCase()} — ` +
                    'rosters are frozen until it is sealed'
                  : 'League stats are active - no gear changes allowed'}
              </div>
            </div>
          </div>
        )}

        {/* Accordion Container */}
        <div className="accordion-container">
          {/* Player Tabs */}
          {teamPlayers.map((player, index) => (
            <PlayerAccordionTab
              key={player.id}
              player={player}
              gear={organizedGear[player.character_sheet_id] || []}
              isExpanded={expandedTabs.has(index)}
              onTabClick={() => handleTabClick(index, player.character_sheet_id)}
              canModifyGear={canModifyGear}
              onUnequip={handleUnequipGear}
              teamGear={teamGear}
              onTransferClick={handleTransferClick}
              isInTransferMode={transferMode.active}
              tabState={tabStates[player.character_sheet_id]}
              transferGearId={transferMode.gearId}
              swappableGearIds={transferMode.swappableGearIds}
              onSwapClick={handleGearSwap}
            />
          ))}

          {/* UNHELD Tab */}
          <UnheldAccordionTab
            gear={organizedGear['UNHELD'] || []}
            isExpanded={expandedTabs.has(teamPlayers.length)}
            onTabClick={() => handleTabClick(teamPlayers.length, 'UNHELD')}
            canModifyGear={canModifyGear}
            teamPlayers={teamPlayers}
            onEquip={handleEquipGear}
            teamGear={teamGear}
            onTransferClick={handleTransferClick}
            isInTransferMode={transferMode.active}
            tabState={tabStates['UNHELD']}
            transferGearId={transferMode.gearId}
            swappableGearIds={transferMode.swappableGearIds}
            onSwapClick={handleGearSwap}
          />
        </div>
      </div>

      {/* Tutorial Popup */}
      {showTutorial && (
        <TutorialPopup
          tutorial={tutorialData}
          onClose={handleCloseTutorial}
          onMarkViewed={handleMarkTutorialViewed}
        />
      )}
    </div>
  );
}

export default GearPage;